import React, {useEffect, useRef, useState} from "react";
import {Popover, PopoverButton, PopoverPanel} from "@headlessui/react";
import DatePicker, {registerLocale} from "react-datepicker";
import {format} from "date-fns";
import {ptBR} from "date-fns/locale/pt-BR";
import {FaChevronDown, FaXmark} from "react-icons/fa6";
import "react-datepicker/dist/react-datepicker.css";
import "./card-date-picker.css";
import Button from "../../../components/button";

registerLocale("pt-BR", ptBR);

const parseDate = value => value ? new Date(`${value}T12:00:00`) : null;
const toInputDate = date => date ? format(date, "yyyy-MM-dd") : "";
const fromInputDate = value => value ? parseDate(value) : null;

const CardDatePicker = ({card, onSave, onRemove, openOnMount = false, onOpenOnMount, readOnly = false}) => {
    const triggerRef = useRef(null);
    const hasOpenedOnMountRef = useRef(false);
    const [startEnabled, setStartEnabled] = useState(Boolean(card.startDateValue));
    const [dueEnabled, setDueEnabled] = useState(Boolean(card.dueDateValue));
    const [startDate, setStartDate] = useState(() => parseDate(card.startDateValue));
    const [dueDate, setDueDate] = useState(() => parseDate(card.dueDateValue));
    const [dueTime, setDueTime] = useState(card.dueTime || "12:00");
    const [reminder, setReminder] = useState(card.reminder || (card.reminderEnabled ? "1-day" : "none"));

    useEffect(() => {
        setStartEnabled(Boolean(card.startDateValue));
        setDueEnabled(Boolean(card.dueDateValue));
        setStartDate(parseDate(card.startDateValue));
        setDueDate(parseDate(card.dueDateValue));
        setDueTime(card.dueTime || "12:00");
        setReminder(card.reminder || (card.reminderEnabled ? "1-day" : "none"));
    }, [card.dueDateValue, card.dueTime, card.reminder, card.reminderEnabled, card.startDateValue]);

    useEffect(() => {
        if (!openOnMount || hasOpenedOnMountRef.current) return;
        hasOpenedOnMountRef.current = true;
        triggerRef.current?.click();
        onOpenOnMount?.();
    }, [onOpenOnMount, openOnMount]);

    const summary = (() => {
        if (!startEnabled && !dueEnabled) return "Definir datas";
        const startText = startEnabled && startDate ? format(startDate, "d 'de' MMM", {locale: ptBR}) : "";
        const dueText = dueEnabled && dueDate ? format(dueDate, "d 'de' MMM", {locale: ptBR}) : "";
        if (startText && dueText) return `${startText} – ${dueText}${dueTime ? `, ${dueTime}` : ""}`;
        return `${startText || dueText}${dueEnabled && dueTime ? `, ${dueTime}` : ""}`;
    })();

    if (readOnly) return (
        <section className="relative w-fit max-w-full">
            <h4 className="mb-1.5 text-xs font-semibold text-slate-500">Datas</h4>
            <div className="flex h-8 max-w-full items-center rounded-md border border-slate-200 bg-slate-100 px-3 text-sm text-slate-600">
                <span className="truncate">{summary}</span>
            </div>
        </section>
    );

    const saveDates = (overrides = {}) => {
        const values = {startEnabled, dueEnabled, startDate, dueDate, dueTime, reminder, ...overrides};
        onSave({
            startDateValue: values.startEnabled && values.startDate ? toInputDate(values.startDate) : "",
            dueDateValue: values.dueEnabled && values.dueDate ? toInputDate(values.dueDate) : "",
            dueDate: values.dueEnabled && values.dueDate ? format(values.dueDate, "dd/MM/yyyy") : "",
            dueTime: values.dueEnabled && values.dueDate ? values.dueTime : "",
            reminder: values.reminder,
            reminderEnabled: values.reminder !== "none" && values.dueEnabled && Boolean(values.dueDate),
        });
    };

    const handleCalendarChange = value => {
        if (startEnabled) {
            const [nextStart, nextDue] = value;
            setStartDate(nextStart);
            setDueDate(nextDue);
            setDueEnabled(Boolean(nextDue));
            if (nextDue) saveDates({startDate: nextStart, dueDate: nextDue, dueEnabled: true});
            return;
        }
        setDueDate(value);
        setDueEnabled(Boolean(value));
        saveDates({dueDate: value, dueEnabled: Boolean(value)});
    };

    const removeDates = close => {
        onRemove();
        close();
    };

    return (
        <section className="group relative w-fit max-w-full">
            <div className="mb-1.5 flex items-center gap-2">
                <h4 className="text-xs font-semibold text-slate-500">Datas</h4>
                <button type="button" onClick={onRemove} className="rounded p-0.5 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 focus:opacity-100" title="Excluir datas" aria-label="Excluir datas"><FaXmark /></button>
            </div>

            <Popover>
                <PopoverButton ref={triggerRef} className="flex h-8 max-w-full items-center gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 text-sm text-slate-600 transition hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200">
                    <span className="truncate">{summary}</span><FaChevronDown className="shrink-0 text-[10px]" />
                </PopoverButton>

                <PopoverPanel anchor={{to: "bottom start", gap: 8, padding: 12}} portal className="z-[70] max-h-[calc(100dvh-1.5rem)] w-[340px] max-w-[calc(100vw-1.5rem)] scroll-pb-3 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 shadow-2xl">
                    {({close}) => (
                        <>
                            <div className="mb-3 flex items-center justify-between">
                                <h5 className="text-sm font-semibold text-slate-700">Datas</h5>
                                <button type="button" onClick={close} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Fechar seletor de datas"><FaXmark /></button>
                            </div>

                            <div className="w-full">
                                {startEnabled ? (
                                    <DatePicker inline selectsRange startDate={startDate} endDate={dueDate} onChange={handleCalendarChange} locale="pt-BR" calendarClassName="card-date-picker-calendar" />
                                ) : (
                                    <DatePicker inline selected={dueDate} onChange={handleCalendarChange} locale="pt-BR" calendarClassName="card-date-picker-calendar" />
                                )}
                            </div>

                            <div className="mt-4 space-y-3">
                                <label className="block">
                                    <span className="mb-1 block text-xs font-semibold text-slate-600">Data de início</span>
                                    <span className="flex items-center gap-2">
                                        <input type="checkbox" checked={startEnabled} onChange={({target}) => {
                                            const nextDate = target.checked ? (startDate || dueDate || new Date()) : null;
                                            setStartEnabled(target.checked);
                                            setStartDate(nextDate);
                                            saveDates({startEnabled: target.checked, startDate: nextDate});
                                        }} className="h-4 w-4 accent-blue-600" />
                                        <input type="date" value={toInputDate(startDate)} onChange={({target}) => {
                                            setStartDate(fromInputDate(target.value));
                                            setStartEnabled(Boolean(target.value));
                                        }} onBlur={({target}) => saveDates({startDate: fromInputDate(target.value), startEnabled: Boolean(target.value)})} disabled={!startEnabled} className="h-9 min-w-0 flex-1 rounded-md border border-slate-300 px-2 text-sm text-slate-700 disabled:bg-slate-100 disabled:text-slate-400" />
                                    </span>
                                </label>

                                <label className="block">
                                    <span className="mb-1 block text-xs font-semibold text-slate-600">Data de entrega</span>
                                    <span className="flex items-center gap-2">
                                        <input type="checkbox" checked={dueEnabled} onChange={({target}) => {
                                            const nextDate = target.checked ? (dueDate || startDate || new Date()) : null;
                                            setDueEnabled(target.checked);
                                            setDueDate(nextDate);
                                            saveDates({dueEnabled: target.checked, dueDate: nextDate});
                                        }} className="h-4 w-4 accent-blue-600" />
                                        <input type="date" value={toInputDate(dueDate)} onChange={({target}) => {
                                            setDueDate(fromInputDate(target.value));
                                            setDueEnabled(Boolean(target.value));
                                        }} onBlur={({target}) => saveDates({dueDate: fromInputDate(target.value), dueEnabled: Boolean(target.value)})} disabled={!dueEnabled} className="h-9 min-w-0 flex-1 rounded-md border border-slate-300 px-2 text-sm text-slate-700 disabled:bg-slate-100 disabled:text-slate-400" />
                                        <input type="time" value={dueTime} onChange={({target}) => setDueTime(target.value)} onBlur={({target}) => saveDates({dueTime: target.value})} disabled={!dueEnabled} className="h-9 w-24 rounded-md border border-slate-300 px-2 text-sm text-slate-700 disabled:bg-slate-100 disabled:text-slate-400" />
                                    </span>
                                </label>

                                {/*<label className="block text-xs font-semibold text-slate-600">*/}
                                {/*    Definir lembrete*/}
                                {/*    <select value={reminder} onChange={({target}) => setReminder(target.value)} disabled={!dueEnabled} className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm font-normal text-slate-700 disabled:bg-slate-100 disabled:text-slate-400">*/}
                                {/*        {reminderOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}*/}
                                {/*    </select>*/}
                                {/*</label>*/}

                                {/*<p className="text-xs leading-5 text-slate-500">Os lembretes serão enviados aos membros e seguidores deste cartão.</p>*/}
                                <Button type="button" onClick={() => removeDates(close)} col="w-full !border-slate-300 !text-slate-600">Remover</Button>
                            </div>
                        </>
                    )}
                </PopoverPanel>
            </Popover>
        </section>
    );
};

export default CardDatePicker;
